import { AnsweringClient } from "../clients/answeringClient";

export interface IConsentTerm {
  type: string;
  title: string;
  description: string;
  allow_audio: boolean;
}

export interface ConsentControllerDelegate {
  didAnswerConsent(): void;
  didGetConsentTerm(term: IConsentTerm): void;
  didGetAssignmentToken(token: string): void;
  applicationShouldLogout(): void;
  genericError(message?: string): void;
}

export class ConsentController {

  constructor (
    private delegate: ConsentControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) { }

  public async getConsent(accessToken: string, assignmentToken: string) {
    try {
      const response = await this.answeringClient.getConsentTerm(accessToken, assignmentToken);
      this.delegate.didGetConsentTerm(response.data);
    } catch (error: any) {
      if(error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if(!newTokens) {
          return;
        }
        this.getConsent(newTokens.accessToken, assignmentToken);
        return;
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  public async answerConsent(accessToken: string, assignmentToken: string, acceptedConsent: boolean) {
    try {
      await this.answeringClient.answerConsent(accessToken, assignmentToken, acceptedConsent);
      this.delegate.didAnswerConsent();
    } catch (error: any) {
      if(error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if(!newTokens) {
          return;
        }
        this.answerConsent(newTokens.accessToken, assignmentToken, acceptedConsent);
        return;
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  public async getAssignmentToken(accessToken: string, instrumentId: string) {
    try {
      const assignmentToken = await this.answeringClient.getInstrumentToken(accessToken, instrumentId);
      const data = assignmentToken.data;
      this.delegate.didGetAssignmentToken(data.token);
    } catch (error: any) {
      if(error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if(!newTokens) {
          return;
        }
        this.getAssignmentToken(newTokens.accessToken, instrumentId);
        return;
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  private async refreshTokens(): Promise<{accessToken: string, refreshToken: string} | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if(!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: {accessToken: string, refreshToken: string} = response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    } 
  }

  private extractErrorMessage(error: any): string | undefined {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message
    );
  }
}
